import { AttachMoney, Cancel as CancelIcon, CheckCircle, Close, CreditCard, FileUpload, HourglassEmpty, ReceiptLong, Visibility } from "@mui/icons-material";
import { Box, Chip, Grid, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { format } from "date-fns";
import { IInstallmentProposal } from "../../../../../shared/services/api/proposal/ProposalService";
import { globalFormatCurrencyBRL, globalTranslatePayment } from "../../../../../shared/utils";

interface Props {
	row: IInstallmentProposal;
	isSuperAdmin: boolean;
	onCancel: (id: string) => void;
	onView: (row: IInstallmentProposal) => void;
}

export const InstallmentCard = ({ row, isSuperAdmin, onCancel, onView }: Props) => {
	const theme = useTheme();

	const statusColors = {
		paid: { bg: "#f0fdf4", color: theme.palette.success.main, icon: <CheckCircle /> },
		pending: { bg: theme.palette.background.paper, color: theme.palette.warning.main, icon: <HourglassEmpty /> },
		canceled: { bg: "#fef2f2", color: theme.palette.error.main, icon: <CancelIcon /> },
		billed: { bg: "#eef2ff", color: theme.palette.info.main, icon: <Visibility /> },
		imported: { bg: "#f3f4f6", color: '#383838', icon: <ReceiptLong /> },
	};

	const currentStatus = statusColors[row.status as keyof typeof statusColors];

	const getPaymentLabel = (method: string | undefined) => {
		if (!method) return "Desconhecido";
		if (method.toLowerCase().includes("card")) return "Cartão de Crédito";
		if (method.toLowerCase().includes("billet")) return "Boleto";
		return method;
	};

	const paymentIcon = row.paymentMethod?.toLowerCase().includes("card") ? (
		<CreditCard fontSize="small" sx={{ color: theme.palette.text.secondary }} />
	) : (
		<AttachMoney fontSize="small" sx={{ color: theme.palette.text.secondary }} />
	);

	const renderStatusChip = () => {
		if (!row.status || !currentStatus) return <Box sx={{ width: 70, height: 24 }} />;

		const iconMap = {
			paid: <CheckCircle fontSize="small" sx={{ color: currentStatus.color }} />,
			pending: <HourglassEmpty fontSize="small" sx={{ color: currentStatus.color }} />,
			canceled: <CancelIcon fontSize="small" sx={{ color: currentStatus.color }} />,
			imported: <FileUpload fontSize="small" sx={{ color: currentStatus.color }} />,
			billed: <ReceiptLong fontSize="small" sx={{ color: currentStatus.color }} />,
		};

		const labelMap = {
			paid: "Pago",
			pending: "Pendente",
			canceled: "Cancelada",
			imported: "Importado",
			billed: "Faturado",
		};

		return (
			<Chip
				label={
					<Stack direction="row" alignItems="center" gap={0.5}>
						{iconMap[row.status as keyof typeof iconMap]}
						{labelMap[row.status as keyof typeof labelMap]}
					</Stack>
				}
				size="small"
				sx={{
					fontWeight: 400,
					borderRadius: 2,
					backgroundColor: currentStatus.color + "22",
					color: currentStatus.color,
				}}
			/>
		);
	};

	const actionButtonStyle = {
		backgroundColor: theme.palette.grey[100],
		"&:hover": {
			backgroundColor: theme.palette.grey[200],
		},
		borderRadius: 1.5,
		p: 0.5,
	};

	return (
		<Paper
			sx={{
				px: 2,
				py: 1,
				mb: 2,
				width: "100%",
				backgroundColor: currentStatus?.bg || theme.palette.background.paper,
				borderLeft: `4px solid ${currentStatus?.color || theme.palette.grey[500]}`,
				display: "flex",
				flexDirection: "column",
				gap: 1,
				boxShadow: "rgba(0, 0, 0, 0.1) 0px 0px 5px 0px, rgba(0, 0, 0, 0.1) 0px 0px 1px 0px",
				borderRadius: 2,
			}}
		>
			{/* Título + status fixo para alinhamento */}
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 32 }}>
				<Typography fontWeight={600}>
					{globalTranslatePayment(row.typeAmount)} {row.typeAmount === "parcel" ? row.number : ""}
				</Typography>
				{renderStatusChip()}
			</Stack>

			{/* Informações alinhadas em Grid */}
			<Grid container alignItems="center" spacing={2}>
				{/* Tipo de pagamento */}
				<Grid item xs={12} sm={3}>
					<Stack direction="row" alignItems="center" gap={1}>
						{paymentIcon}
						<Typography variant="body2" color="text.secondary">
							{getPaymentLabel(row.paymentMethod)}
						</Typography>
					</Stack>
				</Grid>

				{/* Vencimento */}
				<Grid item xs={12} sm={3}>
					<Typography variant="body2">
						Vencimento: <strong>{format(new Date(row.dueDate + " 03:00:00"), "dd/MM/yyyy")}</strong>
					</Typography>
				</Grid>

				{/* Valor */}
				<Grid item xs={12} sm={3} textAlign={"right"}>
					<Typography variant="body2">
						Valor:{" "}
						<strong
							style={{
								color: row.typeAmount === "discount" ? theme.palette.error.main : undefined,
							}}
						>
							{row.typeAmount === "discount" ? `- ${globalFormatCurrencyBRL(row.amount)}` : globalFormatCurrencyBRL(row.amount)}
						</strong>
					</Typography>
				</Grid>

				{/* Ações */}
				<Grid item xs={12} sm={3}>
					<Stack direction="row" justifyContent="flex-end" gap={1}>
						<Tooltip title="Visualizar detalhes">
							<IconButton size="small" onClick={() => onView(row)} sx={actionButtonStyle}>
								<Visibility fontSize="small" />
							</IconButton>
						</Tooltip>

						{isSuperAdmin && row.status === "pending" && (
							<Tooltip title="Cancelar parcela">
								<IconButton size="small" onClick={() => onCancel(row.id)} sx={actionButtonStyle}>
									<Close fontSize="small" sx={{ color: theme.palette.error.main }} />
								</IconButton>
							</Tooltip>
						)}
					</Stack>
				</Grid>
			</Grid>
		</Paper>
	);
};
